import { Page, Text, View, Document, Font, Image } from "@react-pdf/renderer";
import { styles } from "./styles/styles";
import { RecipePdfPrepSteps } from "./RecipePdfPrepSteps";
import {
  RecipePdfTimeAndPortion,
  RecipePdfTimeAndPortionIcons,
} from "./RecipePdfTimeAndPortion";
import { PrintListItem } from "../RecipeSingleView/PDFDownloadButton/PDFDownloadButton";
import { customSanitize } from "@/utils/customSanitize";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";

export interface PdfTemplateElements {
  icons: RecipePdfTimeAndPortionIcons;
  logo: string;
}

Font.register({
  family: "Proxima Nova Regular",
  src: "https://api.kleine-tiptopf.aleksundshantu.com/wp-content/uploads/2024/11/proximanova-regular-webfont.woff",
});

Font.register({
  family: "Proxima Nova Bold",
  src: "https://api.kleine-tiptopf.aleksundshantu.com/wp-content/uploads/2024/11/proximanova-bold-webfont.woff",
});

export const RecipePdfTemplate = ({
  recipes,
  elements,
}: {
  recipes: PrintListItem[];
  elements: PdfTemplateElements;
}) => {
  const { getLabel } = useGlobalContext();
  const rezeptTippLabel = getLabel("tipps");
  const rezeptVegiTippLabel = getLabel("vegitipps");
  const recipe = recipes[0];
  if (!recipe) return null;

  const listRegex = /<li>(.*?)<\/li>/gs;
  const tippArray = recipe.item.rezept_tipp
    ?.match(listRegex)
    ?.map((item) => item.replace(/\r?\n|\r/g, ""));
  const tippStr = customSanitize(recipe.item.rezept_tipp).replace(
    /<[^>]*>/g,
    "",
  );
  const hasTipp = tippStr || (tippArray && tippArray.length > 0);

  const vegiTippStr = customSanitize(recipe.item.rezept_vegitipp).replace(
    /<[^>]*>/g,
    "",
  );
  const vegiTippArray = recipe.item.rezept_vegitipp
    ?.match(listRegex)
    ?.map((item) => item.replace(/\r?\n|\r/g, ""));
  const hasVegitipp =
    vegiTippStr || (vegiTippArray && vegiTippArray.length > 0);

  const TippElement = ({ text }: { text: string }) => {
    return (
      <View style={styles.tippElement}>
        <View style={styles.tippBulletPoint} />
        <Text style={styles.textRegular}>{text.replace(/<[^>]*>/g, "")}</Text>
      </View>
    );
  };

  return (
    <Document title={recipe.item.rezept_titel} author="meintiptopf.ch">
      <Page style={styles.body}>
        {/* TOP HEADER */}
        <View style={styles.tiptopfHeader}>
          {/* @react-pdf/renderer Image does not need alt text */}
          {/*eslint-disable-next-line jsx-a11y/alt-text */}
          <Image style={styles.logo} src={elements.logo} />
        </View>
        {/* HEADER */}
        <View style={styles.header}>
          <View style={styles.headerImgCol}>
            {recipe.base64 ? (
              // @react-pdf/renderer Image does not need alt text
              // eslint-disable-next-line jsx-a11y/alt-text
              <Image style={styles.image} src={recipe.base64} />
            ) : null}
          </View>
          <View style={styles.headerTitleCol}>
            <Text style={styles.title}>{recipe.item.rezept_titel}</Text>
          </View>
        </View>
        {/* CONTENT */}
        <View style={styles.mainView}>
          {/* LEFT SIDE */}
          <View style={styles.leftSide}>
            <RecipePdfTimeAndPortion recipe={recipe} icons={elements.icons} />
            {hasTipp && (
              <>
                <View style={styles.tippView}>
                  <Text style={styles.tippLabel}>{rezeptTippLabel}</Text>
                  {tippArray && tippArray?.length > 0 ? (
                    tippArray.map((tipp, i) => (
                      <TippElement key={i} text={tipp} />
                    ))
                  ) : tippStr ? (
                    <TippElement text={tippStr} />
                  ) : null}
                </View>
              </>
            )}
            {hasVegitipp && (
              <>
                <View style={styles.tippView}>
                  <Text style={styles.tippLabel}>{rezeptVegiTippLabel}</Text>
                  {vegiTippArray && vegiTippArray?.length > 0 ? (
                    vegiTippArray.map((tipp, i) => (
                      <TippElement key={i} text={tipp} />
                    ))
                  ) : vegiTippStr ? (
                    <TippElement text={vegiTippStr} />
                  ) : null}
                </View>
              </>
            )}
          </View>
          {/* // RIGHT SIDE */}
          <RecipePdfPrepSteps recipeDetails={recipe.item} />
        </View>

        <Text style={styles.link} render={() => `${recipe.link}`} fixed />

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};
